import { BlogPost } from "./types";

export const aclararImagenBorrosa: BlogPost = {
  slug: "como-aclarar-imagen-borrosa",
  title: "Cómo aclarar una imagen borrosa: filtros de nitidez y técnicas paso a paso",
  description: "Guía técnica sobre filtros de nitidez para imágenes borrosas. Unsharp Mask, Smart Sharpen, High Pass, y cuándo cada técnica produce mejores resultados.",
  date: "2026-03-24",
  readTime: "8 min",
  keywords: "aclarar imagen borrosa, enfocar imagen, filtro nitidez imagen, sharpen imagen borrosa, unsharp mask, mejorar nitidez foto",
  content: `
## Qué significa "aclarar" o "enfocar" una imagen borrosa

El término "aclarar" en el contexto de imágenes borrosas se refiere a aplicar técnicas de aumento de nitidez (sharpening), no a cambiar el brillo de la imagen. El objetivo es hacer que los bordes y detalles parezcan más definidos.

**Puede hacer:**
- Aumentar el contraste local en los bordes
- Reducir la apariencia de borrosidad suave
- Compensar borrosidad introducida por redimensionado
- Mejorar visualmente fotos ligeramente desenfocadas

**No puede hacer:**
- Recuperar detalles que nunca existieron (imagen de baja resolución)
- Revertir borrosidad severa por movimiento
- Crear información nueva en áreas completamente fuera de foco
- Mejorar imágenes con ruido excesivo (el sharpening también amplifica el ruido)

## Los tres filtros de nitidez principales

### Unsharp Mask (Máscara de nitidez)

El filtro más usado y efectivo para la mayoría de casos de borrosidad suave. Funciona creando una versión difuminada de la imagen y aumentando el contraste entre ella y el original.

Los tres parámetros:

**Cantidad (Amount):** cuánto contraste adicional se aplica en los bordes. Valores típicos: 50-150%. Más de 200% suele producir halos visibles.

**Radio (Radius):** el tamaño en pixels del área alrededor de cada borde donde se aplica el efecto. Valores bajos (0.5-1px) para detalles finos. Valores más altos (2-5px) para bordes gruesos o imágenes grandes.

**Umbral (Threshold):** qué diferencia mínima de tono entre pixels adyacentes activa el efecto. Umbral 0 afecta a todos los pixels. Umbral más alto solo afecta a bordes de alto contraste, preservando áreas suaves (piel, cielo) sin ruido adicional.

**Valores recomendados por tipo de imagen:**

| Tipo de imagen | Cantidad | Radio | Umbral |
|---|---|---|---|
| Foto de retrato (piel) | 80% | 1.0px | 5-10 |
| Foto de paisaje | 120% | 1.2px | 2-4 |
| Producto sobre fondo | 100% | 1.0px | 3-5 |
| Captura de pantalla con texto | 150% | 0.5px | 0-2 |
| Foto de arquitectura | 130% | 1.5px | 2-4 |

### Smart Sharpen (Enfoque inteligente)

Disponible en Photoshop, el Smart Sharpen ofrece más control porque permite especificar el tipo de borrosidad:

- **Lens Blur:** para fotos ligeramente fuera de foco
- **Motion Blur:** para fotos con trazos de movimiento. Puedes especificar el ángulo del movimiento.
- **Gaussian Blur:** para borrosidad suave y uniforme

**Cómo usar Smart Sharpen en Photoshop:**
1. Filtro → Nitidez → Enfoque inteligente
2. Selecciona el tipo de borrosidad en el menú "Eliminar"
3. Ajusta Cantidad (80-150%) y Radio (1-3px)
4. Si hay ruido, usa la pestaña "Sombras/Iluminaciones" para reducir el efecto en zonas oscuras/claras

### High Pass Sharpening (Técnica avanzada)

La técnica High Pass es más controlable. Se aplica en capas en Photoshop:

1. Duplica la capa (Ctrl+J)
2. En la capa superior: Filtro → Otro → Paso alto (High Pass)
3. Ajusta radio: 1-5px (valores bajos para detalles finos, altos para bordes gruesos)
4. Cambia el modo de fusión de la capa a "Luz suave" o "Superposición"
5. Ajusta la opacidad de la capa para controlar la intensidad

Esta técnica solo afecta a los bordes y detalles, sin tocar las áreas planas de color uniforme — ideal para retratos donde quieres nitidez sin ruido en la piel.

## Técnicas específicas por tipo de problema

### Foto ligeramente desenfocada (autofoco falló)

La situación más común: la cámara enfocó ligeramente detrás o delante del sujeto.

**Proceso:**
1. Abre en Photoshop / GIMP / editor de fotos
2. Aplica Unsharp Mask: Cantidad 100%, Radio 1.0px, Umbral 4
3. Si los bordes tienen halos, reduce la cantidad al 80%
4. Si hay ruido amplificado, aplica Reducción de ruido suave antes del sharpening

**En herramientas online:**
- Adobe Express → Editar → Nitidez
- Pixlr E → Filtros → Unsharp Mask con control completo

### Foto con artefactos de compresión JPEG y bordes borrosos

**Proceso (orden importante):**
1. Primero: reduce los artefactos de compresión (Photoshop: Reducir ruido → marcar "Eliminar artefactos JPEG", o GIMP: Filtros → Mejorar → Reducir artefactos del JPEG)
2. Segundo: aplica nitidez con Unsharp Mask (Cantidad 80%, Radio 0.8px, Umbral 5)

El orden importa: si aplicas nitidez primero, también acentúas los artefactos de compresión.

### Foto de texto o documento borroso

1. Aumenta el contraste general (Imagen → Ajustes → Brillo/Contraste o Curvas)
2. Aplica Unsharp Mask agresivo: Cantidad 150-200%, Radio 0.5-0.8px, Umbral 0
3. Evalúa la legibilidad del texto

**Alternativa para documentos con texto negro sobre fondo blanco:** convierte a blanco y negro puro (Imagen → Modo → Mapa de bits → Difusión de umbral). Esto maximiza el contraste del texto haciéndolo blanco o negro puro.

### Foto de retrato con piel suave

Los retratos requieren un balance delicado: quieres nitidez en los ojos, cabello y ropa, pero no necesariamente en la piel.

**Técnica selectiva:**
1. Aplica Unsharp Mask con umbral alto (8-12) para evitar afectar áreas de piel suave
2. O usa la técnica High Pass en una capa con máscara aplicada solo en zonas de detalle (ojos, cabello)

**En apps móviles:**
- Lightroom Mobile: deslizador de Nitidez + Enmascaramiento
- Snapseed: Detalles → Nitidez con pincel selectivo

## Herramientas disponibles según tu plataforma

### En el navegador (sin instalar nada)

**Pixlr E (pixlr.com/e)**
- Editor similar a Photoshop en el navegador
- Filtro → Sharpen, o Filtro → Unsharp Mask con control completo

**Adobe Express (express.adobe.com)**
- Interfaz sencilla
- Deslizador de nitidez con previsualización en tiempo real

### En móvil

**Snapseed (Android/iOS, gratuito)**
- Detalles → Nitidez y Estructura
- Permite aplicar con pincel en zonas específicas
- Probablemente la mejor herramienta móvil gratuita para nitidez

**Lightroom Mobile (Adobe, gratuito con limitaciones)**
- Panel de Detalle con Nitidez, Radio, Detalles y Enmascaramiento
- Excelente para fotografía de calidad

### En escritorio

**GIMP (gratuito)**
- Filtros → Mejorar → Máscara de desenfoque (Unsharp Mask)
- Menos opciones que Photoshop pero completamente funcional

**Photoshop / Lightroom (Adobe, de pago)**
- Smart Sharpen, Clarity, Texture, High Pass
- Super Resolution en Lightroom para upscaling con IA

## Compresión y nitidez: la relación

Si bien el tema principal de este artículo es mejorar imágenes borrosas, es relevante entender la relación con la compresión:

Una imagen que comprimiste demasiado (JPEG al 30-50%) generará artefactos visibles. Si necesitas esa imagen con mejor calidad, tienes dos opciones:
1. Volver al original y comprimirlo con más calidad (la opción siempre preferible)
2. Usar herramientas de reducción de artefactos JPEG para mejorar la versión degradada

Si tienes el original, siempre vuelve a él y recomprime con mayor calidad usando nuestro [compresor de imágenes](/).

## Preguntas frecuentes

### ¿El sharpening es permanente?

El sharpening aplicado en Photoshop y la mayoría de editores es no destructivo si trabajas con capas. Si aplicas y guardas sobreescribiendo el archivo original, sí es permanente. Siempre trabaja con una copia o en modo no destructivo.

### ¿Por qué mi foto se ve aún más artificial después de hacer sharpening?

Probablemente estás viendo el efecto halo (borde blanco alrededor de objetos oscuros que crea el sharpening excesivo). Reduce la Cantidad al 50-80% y el Radio a 0.5-1px.

### ¿El sharpening y la compresión interactúan mal?

Sí. Aplicar sharpening intenso y luego comprimir con JPEG produce artefactos más visibles porque el JPEG comprime peor las áreas de alto contraste que crea el sharpening. Si vas a comprimir, aplica el sharpening después de definir el tamaño final, y con moderación.

### ¿Vale la pena comprar Photoshop solo para mejorar fotos borrosas?

Para uso puntual: no. GIMP (gratuito) o herramientas online como Pixlr cubren el 90% de las necesidades. Para uso profesional recurrente: Lightroom tiene las mejores herramientas de nitidez no destructivas.

### ¿Cuántas veces puedo aplicar sharpening a la misma imagen?

Tecnicamente múltiples veces, pero cada aplicación añade más efecto y puede producir halos y ruido acumulado. La regla: aplica el sharpening una sola vez con los valores correctos en lugar de múltiples veces con valores bajos.

## Conclusión

Mejorar una imagen borrosa mediante filtros de nitidez es efectivo para borrosidades suaves y moderadas. Unsharp Mask con valores de Cantidad 80-130%, Radio 0.8-1.2px, Umbral 3-8 cubre la mayoría de casos con buenos resultados.

Para borrosidades severas, los filtros de nitidez tienen límites claros — el upscaling con IA (Upscayl gratuito, Topaz de pago) produce mejores resultados cuando la imagen está pixelada o tiene resolución insuficiente.

Si la borrosidad de tu imagen proviene de compresión excesiva, el mejor enfoque es volver al original y reexportarlo con mayor calidad usando nuestro [compresor de imágenes](/).
  `
};
